import { defineStore } from "pinia";
import api from "@/services/api";
import type{ Company, CompanyFacilityTypes, MetaDataKey, OperationYear, Sector, SubSector, Unit } from "@/types";


type State = {
  sectors: Sector[];
  subSectors: SubSector[];
  isLoading: boolean;
  company: Company;
  companyFacilityTypes: CompanyFacilityTypes[];
  totalFacilityCount: number;
  operationYears: OperationYear[];
  companyOnboardingStatus: string;
  isFacilityError: boolean;
  facilityErrors: string[];
  facilityErrorIndex: number | null;
  isError: boolean;
  companyMetaDataKeys: MetaDataKey[];
  companyId: string | null;
  units: Unit[];
};


export const useCompanyStore = defineStore({
  id: "company",
  state: (): State => ({
    sectors: [] as Sector[],
    subSectors: [] as SubSector[],
    isLoading: false,
    company: {} as Company,
    companyFacilityTypes: [] as CompanyFacilityTypes[],
    totalFacilityCount: 0,
    operationYears: [] as OperationYear[],
    companyOnboardingStatus: "" as string,
    isFacilityError: false,
    facilityErrors: [],
    facilityErrorIndex: null,
    isError: false,
    companyMetaDataKeys: [] as MetaDataKey[],
    companyId: localStorage.getItem("companyId") || null,
    units: [] as Unit[] 
  }),
  actions: {
    async getCompany(companyId: string | null = null): Promise<void> {
      this.isLoading = true;
      if (!companyId) {
        companyId = localStorage.getItem("companyId");
      }

      try {
        const { data } = await api.get(`/company/${companyId}`);
        this.company = data;
        this.companyOnboardingStatus = data.onboardingStatus;
        this.isLoading = false;
      } catch (e) {}
    },
    async updateCompany(id: string, payload: object) {
      this.isLoading = true;
      await api.put(`/company/${id}`, payload).then((response) => {
        this.getCompany();
        this.isLoading = false;
      });
    },
    async getCompanyFacilityTypes() {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");
      await api.get(`/company/${companyId}/facility-types`).then((response) => {
        const { data } = response;
        this.companyFacilityTypes = data;
        this.totalFacilityCount = this.companyFacilityTypes.reduce(
          (acc, obj) => acc + obj.facilityCount,
          0
        );
        this.isLoading = false;
      });
    },
    updateCompanyFacilityCount(facility: CompanyFacilityTypes, count: number) {
      this.companyFacilityTypes = this.companyFacilityTypes.map((i) => {
        i.facilityCount =
          i.typeName === facility.typeName ? count : i.facilityCount;
        return i;
      });

      this.totalFacilityCount = this.companyFacilityTypes.reduce(
        (acc, obj) => acc + obj.facilityCount,
        0
      );
    },
    async updateCompanyFacilityTypes() {
      this.isLoading = true;
      this.isError = false;

      const payload: CompanyFacilityTypes[] = [];

      this.companyFacilityTypes.forEach((f: CompanyFacilityTypes) => {
        if (f.facilityType !== "CUSTOM") {
          payload.push(f);
        }
        if (f.facilityType === "CUSTOM" && f.typeName !== "") {
          payload.push(f);
        }
      });

      const uniqueValues =
        this.companyFacilityTypes.map((v) => v.typeName).length >
        new Set(this.companyFacilityTypes.map((v) => v.typeName)).size;

      if (uniqueValues) {
        return;
      }

      try {
        const { data } = await api.put(
          `/company/${this.company.id}/facility-types`,
          payload
        );
        this.companyFacilityTypes = data;
        this.isLoading = false;
        this.isError = false;
      } catch (e) {
        this.isLoading = false;
        this.isError = true;
      }
    },
    addCustomFacilityType(typeOrder: number) {
      const companyId = localStorage.getItem("companyId");
      this.companyFacilityTypes.push({
        facilityType: "CUSTOM",
        logicalCompanyId: `${companyId}`,
        facilityCount: 0,
        typeNameLabel: "",
        typeName: "",
        typeOrder,
      });
    },
    setCustomFacilityTypeName(
      name: string,
      index: number,
      existCount: number
    ): boolean {
      if (!name) {
        return false;
      }

      const isContain: CompanyFacilityTypes | undefined =
        this.companyFacilityTypes.find(
          (f) => f.typeName === name && f.typeOrder != index
        );

      this.companyFacilityTypes = this.companyFacilityTypes.map((facility) => {
        facility.typeNameLabel =
          facility.typeOrder === index ? name : facility.typeNameLabel;
        facility.typeName =
          facility.typeOrder === index ? name : facility.typeName;
        facility.facilityCount =
          facility.typeOrder === index ? 1 : facility.facilityCount;
        facility.isError = !!(facility.typeOrder === index && isContain);
        return facility;
      });

      return existCount === 0;
    },

    async deleteCustomFacilityType(typeName: string) {
      const index = this.companyFacilityTypes.findIndex((item) => item.typeNameLabel === typeName);

      if (index !== -1) {
        this.companyFacilityTypes.splice(index, 1);
        this.companyFacilityTypes = [...this.companyFacilityTypes];
      }

    },
    async getSectors() {
      this.isLoading = true;
      await api.get(`/sector`).then((response) => {
        const { data } = response;
        this.sectors = data;
        this.isLoading = false;
      });
    },
    async getSubSectors(id: string) {
      this.isLoading = true;
      await api.get(`/sector/${id}/sub-sector`).then((response) => {
        const { data } = response;
        this.subSectors = data;
        this.isLoading = false;
      });
    },
    async getUnits() {
      this.isLoading = true;
      await api.get("/unit?intensityType=PRODUCTION").then((response) => {
        const { data } = response;
        this.units = data;
        this.isLoading = false;
      });
    },
    async fetchOperationYears() {
      const companyId = localStorage.getItem("companyId");
      try {
        const { data } = await api.get(`company/${companyId}/operation-years`);
        this.operationYears = data;
      } catch (e) {}
    },
    async fetchCompanyMetaData () {
      try {
          const { data } = await api.get(`company/${localStorage.getItem('companyId')}/metadata`)
          this.companyMetaDataKeys = data
      } catch (e) {
          this.companyMetaDataKeys = []
      }
    },
    async updateIntensityUnit(unitId: string) {
      this.isLoading = true;
      try {
        const payload = { id: unitId };
        await api.put(`/company/${localStorage.getItem('companyId')}/intensity-unit`, payload);
        await this.getCompany();
        this.isLoading = false;
        return true;
      } catch (e) {
        this.isLoading = false;
        return false;
      }
    }    
  },
  getters: {
    getFacilityType(state) {
      return state.companyFacilityTypes.sort((a, b) => {
        return a.typeOrder - b.typeOrder;
      });
    },
    getCompanyFacilities({ companyFacilityTypes }): CompanyFacilityTypes[] {
      return companyFacilityTypes.filter(
        (facility) => facility.facilityCount !== 0
      );
    },
    getCompanyInfo({ company }): Company | null {
      if (!company) {
        return company;
      } else {
        return null;
      }
    },
    getOperationYears({ operationYears }): OperationYear[] {
      return operationYears.sort(
        (a: OperationYear, b: OperationYear) => Number(b.year) - Number(a.year)
      );
    },
    companyBaseYear({ operationYears }): string {
      const year = operationYears.find((y) => y.isBaseYear);

      if (year) {
        return year.year;
      }

      return "";
    },
    isErrorForCompanyFacilityTypes({ companyFacilityTypes }): boolean {
      const isError = companyFacilityTypes.find((f) => f.isError);
      return isError != undefined;
    },
    getIsLoading({ isLoading }): boolean {
      return isLoading;
    },
  },
});
